import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { usePatchConnectionType } from '../../hooks/useConnections';
import {
  effectiveConnectionType,
  type Connection,
  type UserSettableConnectionType,
} from '../../types/connection';

// The categories the initiator can set. Mirrors the backend's user-mutable
// allow-list; share_* provenance types are write-once and not offered.
const OPTIONS: readonly UserSettableConnectionType[] = [
  'friend',
  'family',
  'connection',
] as const;

interface ConnectionTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: Connection;
  /**
   * z-index for the picker overlay. Defaults to `z-50` (the profile
   * connections list, where it's the only modal). Pass a higher value when
   * stacking it over another modal (e.g. the connection-status panel).
   */
  zIndexClass?: string;
}

/**
 * Chooser for a connection's type. Picking the option that already matches the
 * displayed category is a no-op (so a write-once share_* type isn't overwritten
 * just because it surfaces as "Connection"); picking a different one patches.
 */
export const ConnectionTypeModal: React.FC<ConnectionTypeModalProps> = ({
  isOpen,
  onClose,
  connection,
  zIndexClass = 'z-50',
}) => {
  const { t } = useTranslation('connections');
  const recategorize = usePatchConnectionType();
  const effective = effectiveConnectionType(connection.type);

  const handleSelect = (type: UserSettableConnectionType) => {
    if (effective === type) {
      onClose();
      return;
    }
    recategorize.mutate({ id: connection.id, type }, { onSuccess: onClose });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass={zIndexClass}>
      <h2 className="mb-3 text-lg font-bold text-foreground">{t('picker.title')}</h2>
      <div className="flex flex-col gap-2">
        {OPTIONS.map((type) => {
          const active = effective === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => handleSelect(type)}
              disabled={recategorize.isPending}
              className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium text-foreground transition-colors disabled:opacity-50 ${
                active ? 'border-brand-primary' : 'border-border hover:bg-muted'
              }`}
            >
              {t(`type.${type}`)}
              {active && <Check className="h-4 w-4 text-brand-primary" />}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={recategorize.isPending}
          className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
        >
          {t('common:actions.cancel')}
        </button>
      </div>
    </Modal>
  );
};
